import { create } from 'zustand';
import { Message } from '@arco-design/web-react';
import { getMyAgent, heartbeatAgent, setAgentState, getBreakConfig, type AgentProfile, type PhoneMode } from '../api/agent';
import { useAuthStore } from './auth';

interface AgentStore {
  loaded: boolean;
  /** 档案所属用户 ID：同一 SPA 会话内换账号登录（无页面刷新）时强制重载，防残留上一账号坐席档案 */
  loadedFor?: number;
  agentId?: number;
  extension?: string;
  sipPassword?: string;
  /** 接听设备：softphone=浏览器软电话（默认）；desk=外部 SIP 话机（不注册软电话，振铃在话机上） */
  phoneMode?: PhoneMode;
  state: string; // idle / busy / break / offline / ringing / talking / after_work
  /** 小休原因（仅 state=break 时非空） */
  breakReasonId?: number;
  breakReason?: string;
  load: (userId: number) => Promise<void>;
  applyProfile: (a: AgentProfile) => void;
  changeState: (state: string, reasonId?: number, reasonName?: string) => Promise<void>;
  /** 服务端 WS agent_state 事件驱动的状态更新（不调用 API，仅本地同步） */
  applyRemoteState: (state: string, reasonId?: number, reason?: string) => void;
  /** 自愈轮询：拉取本人最新状态，仅在有变化时更新（防 WS 事件丢失） */
  refreshState: () => Promise<void>;
  /** 登出/换号时本地复位坐席档案（不调 API） */
  reset: () => void;
}

export const useAgentStore = create<AgentStore>((set, getState) => ({
  loaded: false,
  state: 'offline',
  // GET /agents/me：返回本人坐席档案（含 extension / sip_password / state）
  load: async (userId) => {
    if (getState().loaded && getState().loadedFor === userId) return; // 幂等：同一用户已加载
    // 换账号/首次加载：先清旧档案，SIP 注册与坐席操作不得沿用上一账号数据
    set({ agentId: undefined, extension: undefined, sipPassword: undefined, phoneMode: undefined, state: 'offline', loaded: false });
    try {
      const a = await getMyAgent();
      if (a && a.id) {
        getState().applyProfile(a);
        // 默认上线状态：坐席当前 offline（登出/首登）时按管理配置落默认态（示闲/示忙）
        if (!a.state || a.state === 'offline') {
          try {
            const cfg = await getBreakConfig();
            const def = cfg?.default_online_state;
            if (def === 'idle' || def === 'busy') {
              await getState().changeState(def);
            }
          } catch {
            /* 配置读取失败静默，保持离线 */
          }
        }
      }
    } catch (e) {
      // 非坐席用户（如管理员）没有坐席档案属正常；但真实坐席档案拉取失败会表现为
      // "看不到软电话、也不知为什么"（此前完全静默）——故真有坐席身份时给一次可见提示。
      const user = useAuthStore.getState().user as { extension?: string } | undefined;
      if (user?.extension) {
        Message.warning((e as { msg?: string } | undefined)?.msg || '坐席档案加载失败，软电话可能不可用，可刷新后重试');
      }
    } finally {
      set({ loaded: true, loadedFor: userId });
    }
  },
  applyProfile: (a) =>
    set({
      agentId: a.id,
      extension: a.extension,
      sipPassword: a.sip_password,
      phoneMode: a.phone_mode === 'desk' ? 'desk' : 'softphone',
      state: a.state || 'offline', // 初始坐席状态读档案里的 state
      breakReasonId: a.state === 'break' ? a.break_reason_id : undefined,
      breakReason: a.state === 'break' ? a.break_reason : undefined,
    }),
  changeState: async (state, reasonId, reasonName) => {
    const id = getState().agentId;
    if (!id) return;
    await setAgentState(id, state, reasonId);
    const apply: Partial<AgentStore> = { state };
    if (state === 'break' && reasonId) {
      apply.breakReasonId = reasonId;
      apply.breakReason = reasonName || undefined; // 名称优先本地回填，缺省等心跳/远程事件补齐
    } else {
      apply.breakReasonId = undefined;
      apply.breakReason = undefined;
    }
    set(apply);
  },
  applyRemoteState: (state, reasonId, reason) => {
    const next: Partial<AgentStore> = {};
    if (state && state !== getState().state) next.state = state;
    if (state === 'break') {
      // 小休中换原因（state 未变）也需更新原因
      if (reasonId !== undefined && reasonId !== getState().breakReasonId) {
        next.breakReasonId = reasonId;
        next.breakReason = reason;
      }
    } else if (state && getState().breakReason !== undefined) {
      next.breakReasonId = undefined;
      next.breakReason = undefined;
    }
    if (Object.keys(next).length) set(next);
  },
  // Swapped to heartbeat endpoint: renews lease + returns state (replaces GET /agents/me polling)
  refreshState: async () => {
    try {
      const a = await heartbeatAgent();
      const next: Partial<AgentStore> = {};
      if (a?.state && a.state !== getState().state) next.state = a.state;
      if (a?.state === 'break' && a.break_reason) {
        if (a.break_reason !== getState().breakReason) {
          next.breakReason = a.break_reason;
          next.breakReasonId = a.break_reason_id;
        }
      } else if (getState().breakReason !== undefined) {
        next.breakReason = undefined;
        next.breakReasonId = undefined;
      }
      if (Object.keys(next).length) set(next);
    } catch {
      /* 静默：失败则租约到期后巡检置 offline */
    }
  },
  reset: () =>
    set({
      loaded: false,
      loadedFor: undefined,
      agentId: undefined,
      extension: undefined,
      sipPassword: undefined,
      phoneMode: undefined,
      state: 'offline',
      breakReasonId: undefined,
      breakReason: undefined,
    }),
}));
